<script>import * as d3 from "d3";
import { okhsl } from "../../../../d3-mapp/color/index.js";
import Bar from "./Bar.svelte";
export let tag = "";
export let orientation = "vertical";
export let textPosition = "inside";
export let width;
export let height;
export let padding = 5;
export let defaultFill = okhsl("transparent");
export let data;
export let axisUnit;
export let label = (datum) => datum.label;
export let value = (d) => d.value;
export let barSize = 20;
export let showAxis = true;
export let duration = 400;
const tickPadding = 9;
const barPadding = 0.1;
const categoryPadding = padding;
const scalarAxisSize = showAxis ? 40 : 0;
const fontSize = 10;
const xAxisTitle = orientation === "vertical" ? -scalarAxisSize : width / 2;
const yAxisTitle = orientation === "vertical" ? height / 2 : tickPadding + 2 * fontSize;
const transformAxisTitle = orientation === "vertical" ? `rotate(-90, ${-scalarAxisSize}, ${height / 2})` : "";
let barCount = Object.keys(data.distribution).length;
let series = void 0;
let colors = void 0;
let tickValues;
$: {
  if (data) {
    const keys = Object.values(data.distribution).map((datum) => datum.key).reverse();
    const keyValue = (data2, key) => value(data2.distribution[key]);
    series = d3.stack().keys(keys).value(keyValue)(
      // get value for each series key and stack
      [data]
    );
    const swatchLabels = Object.entries(data.distribution).map((entry) => label(entry[1])).reverse();
    const swatchColors = Object.entries(data.distribution).map((entry) => entry[1].color).reverse();
    colors = d3.scaleOrdinal(swatchLabels, swatchColors);
    tickValues = [series[0][0][0]].concat(series.map((valueCategory) => valueCategory[0][1]));
  }
}
let scalarAxisContainerNode;
let scalarScale;
$: {
  if (series && (!showAxis || scalarAxisContainerNode)) {
    let maxValue = d3.max(series, (d) => d3.max(d, (d2) => d2[1]));
    let minValue = d3.min(series, (d) => d3.min(d, (d2) => d2[0]));
    const scalarExtent = [minValue, maxValue];
    const scalarRange = orientation === "vertical" ? [height - 2 * categoryPadding, 0] : [0, width - 2 * categoryPadding];
    scalarScale = d3.scaleLinear(scalarExtent, scalarRange).nice();
    if (showAxis) {
      const axisGenerator = orientation === "vertical" ? d3.axisLeft(scalarScale) : d3.axisBottom(scalarScale);
      if (tickValues.length <= 4) {
        axisGenerator.tickValues(tickValues).tickFormat(d3.format(".1f"));
      }
      d3.select(scalarAxisContainerNode).call(axisGenerator);
    }
  }
}
const fgColor = (bg) => {
  const light = "#dbe4e7";
  const dark = "#2d2f36";
  return okhsl(bg).l > 0.5 ? dark : light;
};
let barData = [];
let seriesRectDataGenerator = void 0;
$: {
  seriesRectDataGenerator = scalarScale ? orientation === "vertical" ? (data2) => data2.map((datum, i) => {
    return {
      data: datum[0].data.distribution[datum.key],
      index: i,
      value: value(datum[0].data.distribution[datum.key]),
      y: scalarScale(datum[0][1]) + categoryPadding,
      x: scalarAxisSize + barSize * barPadding / 2,
      height: scalarScale(datum[0][0]) - scalarScale(datum[0][1]),
      width: barSize - barSize * barPadding
    };
  }) : (data2) => data2.map((datum, i) => {
    return {
      data: datum[0].data.distribution[datum.key],
      index: i,
      value: value(datum[0].data.distribution[datum.key]),
      x: scalarScale(datum[0][0]) + categoryPadding,
      y: height - scalarAxisSize - barSize - barSize * barPadding / 2,
      width: scalarScale(datum[0][1]) - scalarScale(datum[0][0]),
      height: barSize - barSize * barPadding
    };
  }) : void 0;
}
$: {
  barData = series && seriesRectDataGenerator ? seriesRectDataGenerator(series) : [];
}
barSize;
</script>

<g
  class="bar-chart {tag}"
  transform={`translate(0,0)`}
  stroke="none"
  fill={defaultFill?.formatHex()}
  fill-opacity={defaultFill?.opacity}
>
  {#if series}
    {#if barData.length > 0}
      <g class="bars">
        {#each barData as barDatum (barDatum.data.key)}
        <Bar title={label} {duration} {barDatum} {fgColor} {orientation} {textPosition} {fontSize}/>
        {/each}
      </g>
    {/if}
    {#if showAxis}
    <g
      class={`axis scalar-scale ${orientation === "horizontal" ? "bottom" : "left"}`}
      transform="translate({orientation === 'vertical'
        ? scalarAxisSize
        : categoryPadding},{orientation === 'vertical'
        ? categoryPadding
        : height - scalarAxisSize})"
      bind:this={scalarAxisContainerNode}
      ><text
        y={yAxisTitle}
        x={xAxisTitle}
        transform={transformAxisTitle}
        text-anchor="middle">{axisUnit}</text
      ></g
    >
    {/if}
  {/if}
</g>

<style>
  g.axis.scalar-scale :global(text),
  g.bar-chart :global(text),
  g.bar-chart text {
    fill-opacity: 1;
    font:
      normal 10px/1 Roboto,
      sans-serif;
  }

  g.axis.band-scale :global(text) {
    fill-opacity: 0;
    text-anchor: start;
  }
  g.axis.scalar-scale :global(text),
  g.bar-chart g.bars :global(text) {
    fill: currentColor;
  }
  g.axis.scalar-scale :global(text) {
    font-weight: bold;
  }
</style>
